<script setup lang="ts">
import { useGetMemoryWarningInfo } from "@/composable/useMemoryWarning";
import { reloadApp } from "@/utils/reloadApp";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();
const { showBanner, dismiss } = useGetMemoryWarningInfo();
</script>

<template>
  <div v-if="showBanner" class="memory-banner">
    <div class="banner-content">
      <div class="banner-icon">
        <i class="bi bi-memory"></i>
      </div>
      <div class="banner-text">
        <strong>{{ t("dashboard:sync.page.memoryBanner.title") }}</strong>
        <span>{{ t("dashboard:sync.page.memoryBanner.message") }}</span>
      </div>
    </div>
    <button class="banner-action" @click="reloadApp()">
      <i class="bi bi-arrow-clockwise"></i>
      <span>{{ t("dashboard:sync.page.memoryBanner.action") }}</span>
    </button>
    <button class="banner-close" @click="dismiss" aria-label="Close">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>
</template>

<style scoped>
.memory-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: var(--bs-danger-bg-subtle);
  border-bottom: 2px solid var(--bs-danger);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: var(--bs-danger-border-subtle);
  color: var(--bs-danger-text-emphasis);
  flex-shrink: 0;
}

.banner-icon i {
  font-size: 1.2rem;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.banner-text strong {
  color: var(--bs-danger-text-emphasis);
  font-size: 0.9rem;
}

.banner-text span {
  color: var(--bs-body-color);
  font-size: 0.8rem;
  opacity: 0.85;
}

.banner-action {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background-color: var(--bs-danger);
  border: none;
  border-radius: 0.375rem;
  padding: 0.4rem 0.85rem;
  font-family: inherit;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.banner-action:hover {
  filter: brightness(1.1);
}

.banner-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: var(--bs-danger-text-emphasis);
  opacity: 0.7;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.banner-close:hover {
  opacity: 1;
}

/* Responsive */
@media (max-width: 480px) {
  .memory-banner {
    padding: 0.5rem 0.75rem;
  }

  .banner-icon {
    width: 2rem;
    height: 2rem;
  }

  .banner-icon i {
    font-size: 1rem;
  }

  .banner-text strong {
    font-size: 0.85rem;
  }

  .banner-text span {
    font-size: 0.75rem;
  }

  /* Le libellé sauterait à la ligne et écraserait le message : on garde l'icône seule. */
  .banner-action span {
    display: none;
  }

  .banner-action {
    padding: 0.4rem 0.55rem;
  }
}
</style>
